import React, { useEffect } from 'react';
import { cn } from '../utils/cn';

interface TermsOfUsePageProps {
  theme: 'dark' | 'light';
  onGoHome: () => void;
}

const TermsOfUsePage: React.FC<TermsOfUsePageProps> = ({ 
  theme, 
  onGoHome 
}) => {
  // Автоматический скролл наверх при открытии страницы
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={cn(
      "min-h-screen",
      theme === 'dark'
        ? "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"
        : "bg-gradient-to-br from-gray-50 via-white to-gray-100"
    )}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onGoHome}
            className={cn(
              "inline-flex items-center text-sm font-medium transition-colors mb-4",
              theme === 'dark' 
                ? "text-slate-300 hover:text-white" 
                : "text-gray-600 hover:text-gray-900"
            )}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад на главную
          </button>
          
          <h1 className={cn(
            "text-3xl font-bold mb-2",
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          )}>
            Пользовательское соглашение
          </h1>
          
          <p className={cn(
            "text-sm",
            theme === 'dark' ? 'text-slate-300' : 'text-gray-500'
          )}>
            г. Москва<br />
            Редакция от «__» ________ 2025 г.
          </p>
        </div>

        {/* Content */}
        <div className={cn(
          "prose prose-lg max-w-none",
          theme === 'dark' ? 'prose-invert' : ''
        )}>
          <div className={cn(
            "bg-white rounded-lg shadow-sm border p-8",
            theme === 'dark' ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'
          )}>
            <p className={cn(
              "mb-6 leading-relaxed",
              theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            )}>
              Настоящее Пользовательское соглашение (далее — «Соглашение») является
              публичной офертой в соответствии со статьёй 437 Гражданского кодекса РФ
              и определяет правила использования сервиса Plinus, расположенного по
              адресу https://plinus.ru (далее — «Сервис»).
            </p>

            <p className={cn(
              "mb-6 leading-relaxed",
              theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            )}>
              Оператором Сервиса является ООО «Х8», зарегистрированное по
              законодательству Российской Федерации (далее — «Администрация»).
              Контакт для обращений: help@plinus.ru.
            </p>

            <p className={cn(
              "mb-8 leading-relaxed",
              theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
            )}>
              Используя Сервис, Пользователь подтверждает, что ознакомился с условиями
              настоящего Соглашения и принимает их. Если Пользователь не согласен с
              условиями, он обязан прекратить использование Сервиса.
            </p>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                1. Предмет соглашения
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>1.1.</strong> Администрация предоставляет Пользователю право бесплатного доступа
                  к Сервису Plinus, который с помощью технологий искусственного интеллекта
                  анализирует введённый текстовый запрос и формирует список возможных «за»
                  и «против», а также итоговую рекомендацию.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>1.2.</strong> Сервис носит исключительно информационный характер и предназначен
                  для поддержки принятия решений. Окончательное решение всегда остаётся за
                  Пользователем.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                2. Порядок использования
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>2.1.</strong> Доступ к Сервису предоставляется без регистрации и без оплаты.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>2.2.</strong> Пользователь вправе использовать Сервис только для личных,
                  некоммерческих целей.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>2.3.</strong> Сервис не предназначен для использования детьми без согласия
                  родителей или законных представителей.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                3. Ограничения
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>3.1.</strong> Сервис не является и не может рассматриваться как:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>юридическая консультация;</li>
                  <li>медицинская рекомендация;</li>
                  <li>финансовая или инвестиционная рекомендация;</li>
                  <li>иная профессиональная услуга.</li>
                </ul>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>3.2.</strong> Пользователь обязуется не использовать Сервис для:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>незаконных действий;</li>
                  <li>публикации или передачи вредоносных данных;</li>
                  <li>вмешательства в работу Сервиса.</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                4. Права и обязанности сторон
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>4.1.</strong> Пользователь имеет право:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>свободно пользоваться Сервисом в пределах его функциональности;</li>
                  <li>обращаться в службу поддержки по адресу help@plinus.ru.</li>
                </ul>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>4.2.</strong> Пользователь обязан:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>использовать Сервис добросовестно и законно;</li>
                  <li>не предпринимать действий, направленных на нарушение работоспособности Сервиса.</li>
                </ul>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>4.3.</strong> Администрация имеет право:
                </p>
                
                <ul className={cn(
                  "list-disc list-inside ml-4 space-y-1",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <li>изменять или прекращать работу Сервиса в любое время без предварительного уведомления;</li>
                  <li>обновлять или дополнять функциональность;</li>
                  <li>вносить изменения в настоящее Соглашение.</li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                5. Ограничение ответственности
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>5.1.</strong> Сервис предоставляется «как есть» (as is).
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>5.2.</strong> Администрация не гарантирует абсолютную точность, полноту или
                  применимость результатов анализа.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>5.3.</strong> Администрация не несёт ответственности за решения и действия,
                  предпринятые Пользователем на основании информации, полученной в
                  Сервисе.
                </p>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                6. Интеллектуальные права
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>6.1.</strong> Все права на Сервис, его программный код, дизайн, доменное имя,
                  логотип и иные элементы принадлежат ООО «Х8».
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>6.2.</strong> Пользователь не вправе копировать, распространять, изменять или
                  использовать материалы Сервиса без письменного согласия Администрации.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                7. Срок действия и изменения
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>7.1.</strong> Настоящее Соглашение вступает в силу с момента первого
                  использования Сервиса и действует бессрочно.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>7.2.</strong> ООО «Х8» вправе вносить изменения в настоящее Соглашение.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>7.3.</strong> Актуальная версия Соглашения всегда доступна на сайте
                  https://plinus.ru.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                8. Применимое право
              </h2>
              
              <div className="space-y-4">
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>8.1.</strong> Настоящее Соглашение регулируется законодательством Российской
                  Федерации.
                </p>
                
                <p className={cn(
                  "leading-relaxed",
                  theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                )}>
                  <strong>8.2.</strong> Все споры и разногласия, возникающие в связи с настоящим
                  Соглашением, подлежат рассмотрению в судах по месту нахождения ООО «Х8».
                </p>
              </div>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className={cn(
                "text-xl font-semibold mb-4",
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              )}>
                9. Реквизиты
              </h2>
              
              <div className={cn(
                "bg-gray-50 rounded-lg p-6",
                theme === 'dark' ? 'bg-slate-700' : 'bg-gray-50'
              )}>
                <p className={cn(
                  "font-semibold mb-4",
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                )}>
                  ООО «Х8»
                </p>
                
                <div className="space-y-2 text-sm">
                  <p className={cn(
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  )}>
                    <strong>Юридический адрес:</strong> 127137, г. Москва, ул. Правды, д. 24, стр. 2, эт. 1, пом. 1, ком. 144
                  </p>
                  
                  <p className={cn(
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  )}>
                    <strong>ИНН:</strong> 7716986250, <strong>КПП:</strong> 771601001
                  </p>
                  
                  <p className={cn(
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  )}>
                    <strong>Р/с:</strong> 40702810002260004726 в АО «АЛЬФА-БАНК»
                  </p>
                  
                  <p className={cn(
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  )}>
                    <strong>БИК:</strong> 044525593, <strong>к/с:</strong> 30101810200000000593
                  </p>
                  
                  <p className={cn(
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  )}>
                    <strong>E-mail:</strong> help@plinus.ru
                  </p>
                  
                  <p className={cn(
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-700'
                  )}>
                    <strong>Генеральный директор:</strong> Елян Марина Владиславовна
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUsePage;
